import { useState } from 'react';
import api from '../../hooks/useApi';
import Modal from '../shared/Modal';

const EMPTY = { password: '', first_name: '', last_name: '', email: '', phone: '', notes: '', send_email: false, supplier_id: '' };

function generatePassword() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$';
  let pw = '';
  const arr = new Uint8Array(12);
  crypto.getRandomValues(arr);
  arr.forEach(b => { pw += chars[b % chars.length]; });
  return pw;
}

// Add an external contractor: supplier first (every contractor belongs to
// one), then their portal login. Opened from the Contractor Dashboard header,
// or from a supplier's panel with `supplierId` already chosen. `onCreated`
// receives the new contractor's id so the dashboard can open their record,
// which is where the first contract is added.
export default function AddContractorModal({ supplierId = '', suppliers: allSuppliers, onSuppliersChanged, onCreated, onClose }) {
  const [createForm, setCreateForm] = useState(() => ({ ...EMPTY, password: generatePassword(), supplier_id: supplierId }));
  const [createError, setCreateError] = useState('');
  const [creating, setCreating]     = useState(false);
  const [showPw, setShowPw]         = useState(false);
  // Inline supplier creation
  const [createShowNewSupplier, setCreateShowNewSupplier] = useState(false);
  const [createNewSupplierName, setCreateNewSupplierName] = useState('');
  const [createNewSupplierSaving, setCreateNewSupplierSaving] = useState(false);

  const createWorker = async () => {
    setCreateError('');
    if (!createForm.supplier_id) {
      setCreateError('Select or create a supplier first — every contractor must belong to a supplier.');
      return;
    }
    if (!createForm.email || !createForm.first_name || !createForm.last_name) {
      setCreateError('Email, first name and last name are required.');
      return;
    }
    if (!createForm.password) {
      setCreateError('A password is required.');
      return;
    }
    setCreating(true);
    try {
      // username = email — email is the login identifier
      const res = await api.post('resource-workers', {
        ...createForm,
        username: createForm.email.trim().toLowerCase(),
        supplier_id: createForm.supplier_id || null,
      });
      onCreated?.(res?.id);
    } catch (err) {
      setCreateError(err.message?.includes('already exists') ? err.message : (err.message || 'Failed to create resource'));
    } finally {
      setCreating(false);
    }
  };

  async function createInlineSupplierForContractor() {
    if (!createNewSupplierName.trim()) return;
    setCreateNewSupplierSaving(true);
    try {
      const created = await api.post('suppliers', { name: createNewSupplierName.trim(), supplies_resources: 1 });
      const fresh = await api.get('suppliers');
      onSuppliersChanged?.(fresh || []);
      setCreateForm(f => ({ ...f, supplier_id: created.id }));
      setCreateShowNewSupplier(false);
      setCreateNewSupplierName('');
    } catch (err) {
      setCreateError(err.message || 'Failed to create supplier.');
    } finally {
      setCreateNewSupplierSaving(false);
    }
  }

  return (
    <Modal
      title="Add External Contractor"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 text-[10px] font-semibold border border-gray-200 rounded-lg bg-white text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={createWorker} disabled={creating} className="px-6 py-2 text-[10px] font-bold rounded-lg bg-navy text-white hover:bg-navy/90 disabled:opacity-40 transition-colors">
            {creating ? 'Creating…' : createForm.send_email ? '✉ Create & Send Welcome Email' : 'Create Resource'}
          </button>
        </>
      }
    >
        <div className="space-y-4">
          <p className="text-[9px] text-gray-500">Contractor → Supplier → Contracts/SOWs. Email is used to log into the portal.</p>
          {createError && <div className="text-[9px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{createError}</div>}

          {/* Supplier — first and required */}
          <div className={`rounded-xl border-2 p-3 transition-colors ${createForm.supplier_id ? 'border-emerald-300 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/40'}`}>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[8px] font-bold text-gray-600 uppercase tracking-wide">
                Supplier *
                {createForm.supplier_id && <span className="ml-1 text-emerald-600">✓</span>}
              </label>
              {!createShowNewSupplier && !createForm.supplier_id && (
                <button type="button" onClick={() => setCreateShowNewSupplier(true)}
                  className="text-[8px] text-gold font-semibold hover:underline">+ New supplier</button>
              )}
            </div>
            <select className="form-input" value={createForm.supplier_id} onChange={e => { setCreateForm(f => ({...f, supplier_id: e.target.value})); setCreateShowNewSupplier(false); }}>
              <option value="">— select supplier —</option>
              {allSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            {createShowNewSupplier && !createForm.supplier_id && (
              <div className="mt-2 flex items-center gap-2">
                <input className="form-input flex-1 text-[10px]" placeholder="New supplier name…" autoFocus
                  value={createNewSupplierName} onChange={e => setCreateNewSupplierName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && createInlineSupplierForContractor()} />
                <button type="button" onClick={createInlineSupplierForContractor} disabled={!createNewSupplierName.trim() || createNewSupplierSaving}
                  className="px-2 py-1.5 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/90 disabled:opacity-40 whitespace-nowrap">
                  {createNewSupplierSaving ? 'Creating…' : 'Create'}
                </button>
                <button type="button" onClick={() => { setCreateShowNewSupplier(false); setCreateNewSupplierName(''); }}
                  className="text-[9px] text-gray-400 hover:text-gray-600">✕</button>
              </div>
            )}
            <p className="text-[7px] text-gray-400 mt-1.5">The company that employs this contractor. Suppliers are managed in Procurement → Suppliers.</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase tracking-wide mb-1">First Name *</label>
              <input className="form-input" placeholder="e.g. James" value={createForm.first_name} onChange={e => setCreateForm(f => ({...f, first_name: e.target.value}))} />
            </div>
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase tracking-wide mb-1">Last Name *</label>
              <input className="form-input" placeholder="e.g. Smith" value={createForm.last_name} onChange={e => setCreateForm(f => ({...f, last_name: e.target.value}))} />
            </div>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase tracking-wide mb-1">Email (portal login) *</label>
            <input className="form-input" type="email" placeholder="contractor@example.com" value={createForm.email} onChange={e => setCreateForm(f => ({...f, email: e.target.value}))} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase tracking-wide mb-1">Phone</label>
            <input className="form-input" placeholder="+44..." value={createForm.phone} onChange={e => setCreateForm(f => ({...f, phone: e.target.value}))} />
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[8px] font-bold text-gray-500 uppercase tracking-wide">Generated Password *</label>
              <button type="button" onClick={() => setCreateForm(f => ({...f, password: generatePassword()}))} className="text-[8px] text-blue-600 hover:text-blue-800 font-semibold">↻ Regenerate</button>
            </div>
            <div className="flex items-center gap-2">
              <input type={showPw ? 'text' : 'password'} className="form-input flex-1 font-mono text-[11px] bg-white"
                value={createForm.password} onChange={e => setCreateForm(f => ({...f, password: e.target.value}))} />
              <button type="button" onClick={() => setShowPw(v => !v)} className="text-[8px] text-gray-400 hover:text-gray-600 px-2 py-1.5 border border-gray-200 rounded bg-white whitespace-nowrap">
                {showPw ? 'Hide' : 'Show'}
              </button>
            </div>
            <p className="text-[7.5px] text-gray-400 mt-1">Auto-generated — share securely with the resource.</p>
          </div>
          <div
            className={`rounded-xl border-2 p-3 cursor-pointer transition-all ${createForm.send_email ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-gray-50 hover:border-gray-300'}`}
            onClick={() => setCreateForm(f => ({...f, send_email: !f.send_email}))}>
            <div className="flex items-start gap-3">
              <div className={`mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${createForm.send_email ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 bg-white'}`}>
                {createForm.send_email && <span className="text-white text-[8px] font-bold">✓</span>}
              </div>
              <div>
                <div className="text-[9px] font-bold text-gray-700">Email credentials to resource</div>
                <div className="text-[8px] text-gray-500 mt-0.5">Send a welcome email with their portal link, email address and password.</div>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase tracking-wide mb-1">Notes</label>
            <textarea className="form-input" rows={2} placeholder="Optional notes..." value={createForm.notes} onChange={e => setCreateForm(f => ({...f, notes: e.target.value}))} />
          </div>
        </div>
    </Modal>
  );
}
